import { Component, ElementRef, EventEmitter, Injector, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { Subscription, takeUntil } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { BaseComponent } from 'src/app/common/base/base.component';
import { Document, DocumentCategory, DocumentType, RelatedDocument } from 'src/app/services/documents/documents';
import { AbstractControl, FormControl } from '@angular/forms';
import { FormStatusEnum, FormType } from 'src/app/common/enumerations/enumerations';
import { utils } from 'src/app/modules/libs/utils';
import { Router } from '@angular/router';

@Component({
  selector: 'related-documents',
  templateUrl: './related-documents.component.html',
  styleUrls: ['./related-documents.component.scss']
})
export class RelatedDocumentsComponent extends BaseComponent implements OnChanges, OnInit, OnDestroy {
  @Input() relatedDocuments?: RelatedDocument[] | null;
  @Input() disabled?: boolean | null;
  @Input() formID?: number;
  @Input() formType?: FormType;
  @Input() hilite?: boolean | null;
  @Input() label?: string;
  @Input() disableTooltips?: boolean;
  @Input() filterBy?: FormType;
  @Input() control?: FormControl | null;
  @Input() excludeDocuments?: RelatedDocument[];
  @Input() excludeSelf?: boolean;
  @Input() maxItems: number = 0;
  @Input() reviewSCF?: boolean;
  @Input() reviewRSSWA?: boolean;
  @Input() reviewRSF?: boolean;
  /** Show Draft forms in the dropdown. Hidden by default; the review board links pre-active forms so it opts in. */
  @Input() includeDrafts?: boolean;

  @Output() changed = new EventEmitter<RelatedDocument[]>();

  @ViewChild("autoDoc") matAutocompleteDoc!: MatAutocomplete;
  @ViewChild("documentInput") documentInput!: ElementRef<HTMLInputElement>;

  public get documentCtrl(): AbstractControl | null { return this.formGroup?.get("documentCtrl"); }

  documents?: Document[];
  documentsFiltered?: Document[];
  documents$!: Observable<Document[]>;
  documentsSubs!: Subscription;
  documentTypes?: DocumentType[];

  filter: string = '';

  constructor(
    protected override injector: Injector,
    private router: Router
  ) {
    super(injector);
  }

  ngOnInit(): void {

  }

  // trackBy helpers so the chip/option lists reuse DOM when rebuilt.
  // (item typed `any` so each is assignable to the list's TrackByFunction<T>.)
  trackByRelatedDoc = (_: number, d: any) =>
    `${d?.relatedDocumentId}-${d?.relatedDocumentTypeId}`;
  trackByDocTypeId = (_: number, t: any) => t?.id;
  trackByDocumentId = (_: number, d: any) => d?.id;

  override ngOnDestroy(): void {
    this.documentsSubs?.unsubscribe();
    super.ngOnDestroy();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.buildForm();
    this.loadDocuments();
  }

  /**
   * Hydrates link display details without removing records that are unavailable in the current document store.
   * Existing links remain authoritative until the user explicitly removes their chip.
   */
  getRelatedinfo() {
    // Preserve existing links when staging filters or loading timing omit their documents from the store.
    this.relatedDocuments = this.relatedDocuments
      ?.filter(r => r?.relatedDocumentId)
      .map(r => {
        const document = this.documents?.find(d => d.type == r.relatedDocumentTypeId && d.formID == r.relatedDocumentId);
        if (!document) return r;

        return {
          ...r,
          cssClass: document.cssClass + '-background',
          relatedDocumentSerialNo: document.serialNo,
          relatedDocumentStatus: document.status,
          relatedDocumentStatusName: document.statusName,
        };
      });
  }

  buildForm() {
    this.formGroup = this.formBuilder.group({
      documentCtrl: [{ value: null }],
    });
    if (this.control)
      this.disabled = this.control.disabled;
    if (this.disabled) this.formGroup.disable(); else this.formGroup.enable();
    if (this.maxItems > 0 && (this.relatedDocuments?.length ?? 0) >= this.maxItems) {
      this.formGroup.disable();
    }
  }

  /**
   * Loads documents from the store and filters them based on specific criteria.
   *
   * - Subscribes to the documents observable from the store.
   * - Finds the main document matching the current form ID and type.
   * - Filters out the main document, checklist templates, drafts, and canceled documents.
   * - Updates the filtered documents list.
   * - Calls helper methods to fetch related info, apply additional filters, and group documents.
   * - Cleans up the subscription on component destroy.
   */
  loadDocuments(): void {
    this.documents$ = this.store.select(state => state.Documents.data);

    this.documentsSubs = this.documents$
      .pipe(takeUntil(this.destroy$)) // cleanup helper subject
      .subscribe(data => {
        if (!data?.length) return;

        this.documents = data.filter(d =>
          !(d.formID === this.formID && d.type === this.formType) &&
          d.category !== DocumentCategory.ChecklistTemplate &&
          (d.status ?? 0) > FormStatusEnum.New &&
          (d.status ?? 0) !== FormStatusEnum.Canceled &&
          (this.reviewSCF ? d.type == FormType.SCF : true) &&
          (this.reviewRSSWA ? d.type == FormType.RSSWA : true) &&
          (this.reviewRSF ? d.type == FormType.RSF : true)
        );

        if (this.filterBy)
          this.documents = this.documents.filter(d => d.type == this.filterBy);
        if (this.excludeSelf) {
          const self = this.documents.find(d => d.formID == this.formID && d.type == this.formType);
          this.documents = this.documents.filter(d => d.id != self?.id);
        }

        this.documentsFiltered = [...this.documents];

        this.getRelatedinfo();
        this.filterDocuments();
        this.createGroups();
      });
  }


  /**
   * Groups filtered documents by their document type.
   *
   * Iterates over `this.documentsFiltered`, organizing documents into a map keyed by document type ID.
   * Each entry in the map contains the document type information and an array of associated documents.
   * After grouping, filters out document types with no documents and sorts the resulting array by document type ID.
   * The final grouped and sorted array is assigned to `this.documentTypes`.
   */
  createGroups() {
    const typeMap = new Map<number, any>();

    // A document refreshed individually (e.g. after a status change pushed over SignalR) may arrive
    // without its documentType object. Borrow it from any sibling of the same type so the form is not
    // silently dropped from the dropdown.
    const typesById = new Map<number, DocumentType>();
    this.documents?.forEach(d => {
      if (d.documentType?.id && !typesById.has(d.documentType.id)) typesById.set(d.documentType.id, d.documentType);
    });

    this.documentsFiltered?.forEach(d => {
      // Skip main document
      if (d.formID === this.formID && (d.type ?? d.documentType?.id) === this.formType) return;

      const documentType = d.documentType ?? (d.type != null ? typesById.get(d.type) : undefined);
      if (!documentType?.id) return;

      // Get or create the entry
      if (!typeMap.has(documentType.id)) {
        typeMap.set(documentType.id, { ...documentType, documents: [] });
      }

      // Add document to the correct type
      typeMap.get(documentType.id).documents.push(d);
    });

    // Keep only those with documents and sort by id
    this.documentTypes = Array.from(typeMap.values())
      .filter(dt => dt.documents.length > 0)
      .sort((a, b) => utils.sort(a.id, b.id, true));
  }

  /**
   * Filters the list of documents by removing those that are already present in the related documents list.
   *
   * Updates the `documentsFiltered` property with documents from `documents` that do not have a matching
   * `formID` and `type` combination in the `relatedDocuments` array.
   *
   * If `documents` is undefined or null, sets `documentsFiltered` to an empty array.
   */


  filterDocuments(): void {
    const docs = this.documents ?? [];

    const relatedIds = new Set(
      this.relatedDocuments?.map(r => this.buildKey(r.relatedDocumentId, r.relatedDocumentTypeId)) ?? []
    );

    const excludedIds = new Set(
      this.excludeDocuments?.map(r => this.buildKey(r.relatedDocumentId, r.relatedDocumentTypeId)) ?? []
    );

    this.documentsFiltered = docs.filter(d => {
      const key = this.buildKey(d.formID, d.type);
      return !relatedIds.has(key) && !excludedIds.has(key);
    });
  }

  private buildKey(id
    ?: number | string, type?: number | string | null): string {
    return `${id}-${type}`;
  }


  /**
   * Filters a list of documents based on the current filter string.
   *
   * If a filter is active, returns only documents whose serial number or description
   * contains the filter text (case-insensitive). If no filter is set, returns all documents.
   *
   * @param documents - The array of documents to filter, or undefined.
   * @returns The filtered array of documents, or the original array if no filter is active.
   */
  filterTypeDocuments(documents?: Document[]) {
    if (this.filter)
      return documents?.filter((doc) => doc.serialNo?.toLowerCase().includes(this.filter) || doc.description?.toLowerCase().includes(this.filter));
    else return documents;
  }

  removeDoc(document: RelatedDocument): void {
    const index = this.relatedDocuments?.indexOf(document) ?? -1;
    if (index >= 0) {
      this.relatedDocuments?.splice(index, 1);
    }
    if (!this.relatedDocuments?.length) this.filter = '';

    this.processChanges();
  }

  openDocument(e: RelatedDocument) {
    const route = `redirect/${e.relatedDocumentSerialNo}`;
    this.router.navigate([route]);
  }

  addDoc(event: MatChipInputEvent): void {
    if (!this.matAutocompleteDoc.isOpen)
      this.processChanges();
  }

  onDocChange(e: any) {
    const code = e.code;
    if (!code?.includes("Arrow")) {
      this.filterDocuments();
      this.filter = (e.target.value as string).toLowerCase();
      this.documentsFiltered = this.documentsFiltered?.filter((doc) => doc.serialNo?.toLowerCase().includes(this.filter) || doc.description?.toLowerCase().includes(this.filter));
      this.createGroups();
    }
  }

  /**
   * Handles the selection of a document from the autocomplete dropdown.
   *
   * Adds the selected document to the `relatedDocuments` array if it does not already exist,
   * based on its ID and type. Updates the filtered documents and document groups,
   * clears the input field, resets the control value, and emits the updated list of related documents.
   *
   * @param event - The autocomplete selection event containing the selected document.
   */
  selectedDoc(event: MatAutocompleteSelectedEvent): void {
    this.relatedDocuments ??= [];

    const selected = event.option.value as Document;

    const newDocument: RelatedDocument = {
      originalDocumentId: this.formID,
      originalDocumentTypeId: this.formType,
      relatedDocumentId: selected.formID,
      relatedDocumentStatus: selected.status,
      relatedDocumentStatusName: selected.statusName,
      relatedDocumentSerialNo: selected.serialNo,
      relatedDocumentTypeId: selected.type ?? 0,
    };

    // Use some() to check by ID instead of object reference
    const alreadyExists = this.relatedDocuments.some(
      d => d.relatedDocumentId === newDocument.relatedDocumentId &&
        d.relatedDocumentTypeId === newDocument.relatedDocumentTypeId
    );

    if (!alreadyExists) {
      this.relatedDocuments.push(newDocument);
      this.getRelatedinfo();
      this.processChanges();
    }

    this.filter = '';
  }


  processChanges() {
    this.documentInput.nativeElement.value = '';
    this.documentCtrl?.setValue(null);
    this.filterDocuments();
    this.createGroups();

    this.changed.emit(this.relatedDocuments ?? []);
  }
}
